import { useEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { useFeature } from '../../lib/features';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, AlertCircle, EyeOff, Loader2, Paperclip, Pencil, Play, Plus, Reply, Upload, Workflow, X } from 'lucide-react';
import { useChannelFeed, useStore } from '../../store/StoreContext';
import { AppCard } from '../../components/ui/AppCard';
import { Button } from '../../components/ui/Button';
import { Sheet } from '../../components/ui/Sheet';
import { Toast } from '../../components/ui/Toast';
import { LinkifiedText } from '../../components/ui/LinkifiedText';
import { ClampedMarkdown, MarkdownReportText } from '../../components/reports/MarkdownReportText';
import { AttachmentView } from '../../components/ui/AttachmentView';
import { SignalValueCard } from '../../components/signal/SignalValueCard';
import { useAvatarImageUrl } from '../../components/ui/AvatarView';
import { followChannel, uploadAttachment, type UploadedAttachment, type SkippedCSVRow } from '../../api/dataApi';
import { useInfiniteScroll } from '../../lib/useInfiniteScroll';
import { useChannelMembership, useChannelMemberPermissions } from '../../lib/useChannelMembership';
import { useChannelTemplates } from '../../lib/useChannelTemplates';
import { CsvMessageImportSheet, CsvImportErrorBanner } from './CsvMessageImportSheet';
import { SaveAsSignalSheet } from './SaveAsSignalSheet';
import { AttachMappingSheet } from './AttachMappingSheet';
import { DEFAULT_BUNCHING_INTERVAL, REPORT_TYPE, type Message, type Template, type TemplateSignal } from '../../types';
import { ROUND_TYPE, deriveRound, isMaskedAnswer, roundActionLabel } from '../../lib/round';
import { isMultiCharSignalIcon } from '../../lib/signalIcon';
import { DEFAULT_SIGNAL_EMOJI } from '../../components/signal/SignalEditorFields';

// Matches the backend's MIME allowlist (modules/storage/transitions/
// limits.go) minus text/plain - the compose bar offers images and PDFs;
// spreadsheets go through the dedicated CSV importer instead.
const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';

// A template's own avatar (uploaded image, or its emoji on a tinted
// background otherwise) - mirrors TemplatePickerScreen.tsx's identical
// ResultRow markup. Its own component (not inlined in the .map() below)
// since useAvatarImageUrl is a hook and needs one call per template, not
// one call shared across the whole list.
function TemplateChip({ template, isSelected, onSelect }: { template: Template; isSelected: boolean; onSelect: () => void }) {
  const imageUrl = useAvatarImageUrl(template.avatarImageKey);
  return (
    <button
      onClick={onSelect}
      title={`${template.title} (${template.nickname})`}
      className={`shrink-0 rounded-full flex items-center gap-1.5 transition-colors ${
        isSelected ? 'bg-blue-600 pl-1 pr-3 py-1' : 'p-0.5 hover:bg-black/5 dark:hover:bg-white/10'
      }`}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0 overflow-hidden"
        style={{ background: imageUrl ? undefined : `${template.colorHex}26` }}
      >
        {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : template.emoji}
      </span>
      {/* Unselected chips are just the image - a whole row of
          "Template1 (@template1)"-style text labels was both wide (fewer
          templates fit before scrolling) and redundant with the signal
          grid that shows once you've already picked one. The selected
          chip expands to image + title so it's still clear which one is
          active. */}
      {isSelected && <span className="text-sm font-medium text-white whitespace-nowrap">{template.title}</span>}
    </button>
  );
}

// Reports have no per-message emoji of their own (unlike a template signal,
// whose text is always "<emoji> <label>" - see dispatchSignal below) - one
// standard icon for every report, rather than SignalMedallion falling
// through to splitSignalEmoji and grabbing the report's first word instead.
const REPORT_EMOJI = '📝';

// Signals sent before both clients started stamping sender/date (see
// backend/workflows/signal/create.wsl's StoreSender/StoreDate states) come
// back from the backend with an empty date string - render a placeholder
// rather than "Invalid Date". new Date('').toDateString() is consistently
// "Invalid Date" for every unparseable value, so these all group under one
// "Unknown date" separator instead of one per message (see isSameDay).
function formatDate(iso: string) {
  if (!iso) return 'Unknown date';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Older signal messages (sent before dispatchSignal stopped appending the
// template's title - see its comment below) still have " · <title>" baked
// into their stored text, since that's literal history the backend can't
// retroactively rewrite. Stripped at display time instead, scoped to
// signal messages only (message.type set) via the *last* " · " so a plain
// typed message containing that sequence is never touched, and a signal
// name that legitimately contained one wouldn't be either.
function displayText(message: Message): string {
  if (!message.type) return message.text;
  const separatorIndex = message.text.lastIndexOf(' · ');
  return separatorIndex === -1 ? message.text : message.text.slice(0, separatorIndex);
}

// The small quoted-anchor block shown at the top of a reply's own bubble
// (and the compose bar's "Replying to" strip below) - just sender +
// truncated text, no tap-to-jump: the anchored message may not even be
// loaded (it's looked up from whatever's already in feedMessages, no
// separate fetch - see MessagesScreen's messagesById comment). `anchor`
// is undefined when it isn't found (deleted, or paged out) - renders a
// quiet placeholder rather than nothing, so a reply doesn't look like an
// ordinary message with no context at all.
function ReplyQuote({
  anchor,
  className = '',
  onClick,
}: {
  anchor?: Message;
  className?: string;
  // Set only when the anchor is actually jumpable (already loaded) - see
  // scrollToMessage. Renders as a plain, non-interactive strip otherwise so
  // "Original message unavailable" doesn't look clickable when it isn't.
  onClick?: () => void;
}) {
  const clickable = !!anchor && !!onClick;
  return (
    <div
      onClick={clickable ? (e: MouseEvent) => { e.stopPropagation(); onClick!(); } : undefined}
      title={clickable ? 'Jump to original message' : undefined}
      className={`flex items-center gap-1.5 rounded-md border-l-2 border-blue-500/40 bg-black/[0.03] dark:bg-white/[0.05] px-2 py-1 text-[11px] text-black/50 dark:text-white/50 ${clickable ? 'cursor-pointer hover:bg-blue-500/10' : ''} ${className}`}
    >
      <Reply size={11} className="shrink-0 opacity-60" />
      {anchor ? (
        <span className="truncate">
          <span className="font-semibold">{anchor.sender}</span>: {displayText(anchor).trim() || '(no text)'}
        </span>
      ) : (
        <span className="italic">Original message unavailable</span>
      )}
    </div>
  );
}

// dispatchSignal always formats a signal message as "<emoji> <label>" -
// split back apart so the medallion badge can show the emoji on its own
// and the label as a normal text line beside it.
function splitSignalEmoji(text: string): { emoji: string; label: string } {
  const spaceIndex = text.indexOf(' ');
  if (spaceIndex === -1) return { emoji: '', label: text };
  return { emoji: text.slice(0, spaceIndex), label: text.slice(spaceIndex + 1) };
}

function isSameDay(isoA: string, isoB: string) {
  return new Date(isoA).toDateString() === new Date(isoB).toDateString();
}

// Message bunching (Settings > Appearance): `message` folds into the same
// stack as `previous` (the chronologically later message already rendered
// directly above it - see showDaySeparator's comment on render order) when
// they're from the same sender (any mix of plain text/signal messages - the
// side, not the kind, is what bunching groups by) and within
// `intervalSeconds` of each other. intervalSeconds <= 0 means bunching is
// off.
function isBunched(message: Message, previous: Message | undefined, intervalSeconds: number): boolean {
  if (intervalSeconds <= 0 || !previous) return false;
  if (message.sender.trim().toLocaleLowerCase() !== previous.sender.trim().toLocaleLowerCase()) return false;
  if (!isSameDay(message.date, previous.date)) return false;
  const gapMs = Math.abs(new Date(previous.date).getTime() - new Date(message.date).getTime());
  return Number.isFinite(gapMs) && gapMs <= intervalSeconds * 1000;
}

// The pill shown between message groups from different calendar days -
// "Today"/"Yesterday" read more naturally than a full date for the common
// case, matching how most chat apps label same-week separators.
function formatDaySeparator(iso: string) {
  if (!iso) return 'Unknown date';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, { dateStyle: 'long' });
}

// Per-bubble timestamp: just the time, always - the date is already
// visible both from the day separator above it and, for the exact date,
// the detail popup (see DetailRow "Sent" below), so repeating it on every
// single bubble is redundant.
function formatBubbleTime(iso: string) {
  if (!iso) return 'Unknown time';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleTimeString(undefined, { timeStyle: 'short' });
}

// Placement and the server-status row must be derived at render time as well
// as during signal hydration. A message object can survive a background
// channel refresh with an older isMine value, while its stored sender remains
// the stable display identity used by this chat.
function messageBelongsToProfile(message: Message, nickname: string): boolean {
  return message.sender.trim().toLocaleLowerCase() === nickname.trim().toLocaleLowerCase();
}

// A synthetic first entry in the template picker, standing in for "no
// template - just type a plain message." Not a real template id, so it can
// never collide with one (template ids come from the backend's nickname
// field, always "@..."-prefixed - see Template.swift/types.ts).
const TEXT_OPTION_ID = '__text__';

export function MessagesScreen({ channelId, focusSignalId }: { channelId: string; focusSignalId?: string }) {
  const {
    channel,
    templates,
    sendMessage,
    sendRoundAction,
    loadMessages,
    loadMoreMessages,
    messagesHasMore,
    isChannelOwner,
    profile,
    isLoggedIn,
    applyChannelStats,
    addChannelToPersonalList,
    syncChannels,
    updateChannel,
  } = useStore();
  const navigate = useNavigate();
  const c = channel(channelId);
  // Per-channel (Channel edit form's "Message bunching" section), not a
  // global preference - a busy automated channel and a slow one-on-one
  // chat want very different windows. Falls back to the same default a
  // brand-new channel gets while c hasn't loaded yet.
  const bunchingInterval = c?.bunchingInterval ?? DEFAULT_BUNCHING_INTERVAL;
  const { messages: feedMessages, hasMore: feedHasMore } = useChannelFeed(channelId);
  const [text, setText] = useState('');
  // Whether this account may post signals into this channel: the backend
  // gate is "owner or accepted member" (see backend/workflows/signal/
  // create.wsl), so a channel reached via Search but not yet followed
  // sees a join prompt instead of the compose bar. Shared with
  // ReportsScreen via this hook so both panes agree.
  const membership = useChannelMembership(c);
  // Reveal-round state (planning poker / hidden voting) derived from the
  // owner's type "round" marker messages in the timeline - see lib/round.ts.
  // Inert unless the channel has the feature enabled (channel edit form).
  const roundsOn = useFeature('channels.reveal_rounds');
  const round = useMemo(
    () => (c?.revealRounds && roundsOn ? deriveRound(feedMessages) : deriveRound([])),
    [c?.revealRounds, roundsOn, feedMessages]
  );
  // For a reply's quoted-anchor preview (below) and the compose bar's
  // "Replying to X" strip - O(1) lookup by id instead of an .find() per
  // rendered message.
  const messagesById = useMemo(() => new Map(feedMessages.map((m) => [m.id, m])), [feedMessages]);
  // Message bunching (Settings > Appearance): runs of consecutive same-
  // sender messages within bunchingInterval seconds of each other collapse
  // into a single overlapping stack (BunchStack below) instead of a bubble
  // each - tapping the stack scatters it open in place, each message
  // landing in its own normal row. Round markers and masked (hidden-round-
  // answer) rows never join a bunch, same as they never merge into their
  // neighbors' day-separator grouping either. Capped at MAX_BUNCH_SIZE: an
  // automated multi-metric sender can post messages seconds apart for
  // hours, which without a cap would fold an entire channel's history into
  // one or two stacks - besides being useless as a stack ("4,000 messages"
  // tells you nothing), that starved the infinite-scroll sentinel of any
  // real rendered height to fill, so it kept auto-firing loadMoreMessages
  // near-continuously trying to reach the bottom of a viewport that
  // bunching had collapsed to a few hundred px, which read as "stuck
  // loading" for a chatty channel's full history.
  const MAX_BUNCH_SIZE = 20;
  const feedGroups = useMemo(() => {
    const groups: Message[][] = [];
    const canBunchMessage = (m: Message) => m.type !== ROUND_TYPE && !isMaskedAnswer(m, round);
    feedMessages.forEach((message, i) => {
      const prev = feedMessages[i - 1];
      const lastGroup = groups[groups.length - 1];
      if (
        i > 0 &&
        lastGroup &&
        lastGroup.length < MAX_BUNCH_SIZE &&
        canBunchMessage(message) &&
        canBunchMessage(prev) &&
        isBunched(message, prev, bunchingInterval)
      ) {
        lastGroup.push(message);
      } else {
        groups.push([message]);
      }
    });
    return groups;
  }, [feedMessages, bunchingInterval, round]);
  // Which bunched group (keyed by its front/newest message id) is currently
  // scattered open. Only one at a time - opening another, or tapping
  // anywhere outside the open one, closes it (see the scroll container's
  // onClick below).
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [roundBusy, setRoundBusy] = useState(false);
  const doRound = async (action: Parameters<typeof sendRoundAction>[1]) => {
    if (!c || roundBusy) return;
    setRoundBusy(true);
    try {
      await sendRoundAction(c.id, action);
    } finally {
      setRoundBusy(false);
    }
  };
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const canPost = !!c && (isChannelOwner(c) || membership === 'accepted');
  // Per-member permission matrix (roadmap 4a-f) - the owner is always
  // unrestricted; a member's stored flags gate the composer. All derived
  // gates fold in canPost, so a non-member still just sees the join card.
  const perms = useChannelMemberPermissions(c);
  const owner = !!c && isChannelOwner(c);
  const canSendText = canPost && (owner || (perms.postMessages && !perms.signalsOnly));
  const canSendSignals = canPost && (owner || (perms.postMessages && !perms.textOnly));
  const attachmentsOn = useFeature('storage.attachments');
  const importOn = useFeature('channels.messages_import');
  const mappingsOn = useFeature('integrations.mappings');
  const canAttach = attachmentsOn && canSendText && (owner || perms.uploadAttachments);
  const canImportCsv = importOn && !!c && (owner || (canPost && perms.postMessages && perms.importCsv));
  // Plain text is the default mode, not whichever template happened to load
  // first or the channel's saved templateIds - the signal picker is opt-in.
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(TEXT_OPTION_ID);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Set when a tapped signal has promptForValue on (see TemplateSignal's
  // comment in types.ts) - holds sending until a value is entered, instead
  // of dispatching immediately like every other signal.
  const [pendingSignal, setPendingSignal] = useState<TemplateSignal | null>(null);
  const [pendingValue, setPendingValue] = useState('');
  const [detailMessage, setDetailMessage] = useState<Message | null>(null);
  // The message a "save as signal" tap was made from (see the idea icon
  // below) - opens SaveAsSignalSheet seeded from it. Null when closed.
  const [signalSourceMessage, setSignalSourceMessage] = useState<Message | null>(null);
  // Compose-bar attachment state: a file uploaded to /storage and waiting
  // to ride out with the next Send, plus the "+" menu / CSV sheet toggles.
  const [pendingAttachment, setPendingAttachment] = useState<UploadedAttachment | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  // The message the compose bar is currently replying to, if any - shown
  // as a small "Replying to X" strip above the input (cleared on send or
  // its own ✕) and threaded into sendMessage as replyToId. Looked up by
  // id from feedMessages for its quoted-preview text; a stale id (its
  // message got deleted, or paged out) just fails that lookup silently.
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [attachMappingOpen, setAttachMappingOpen] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [csvSkippedRows, setCsvSkippedRows] = useState<SkippedCSVRow[]>([]);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const [signalPanelHeight, setSignalPanelHeight] = useState(92);
  const signalResizeRef = useRef<{ startY: number; startHeight: number } | null>(null);

  // The signal picker is a compact, independently resizable panel on
  // mobile. Pointer events cover both mouse dragging and touch dragging in
  // the browser's responsive layout, while the min/max bounds keep the
  // composer and the message list usable at every height.
  const startSignalResize = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    signalResizeRef.current = { startY: event.clientY, startHeight: signalPanelHeight };
  };
  const moveSignalResize = (event: PointerEvent<HTMLDivElement>) => {
    const resize = signalResizeRef.current;
    if (!resize) return;
    // Moving the splitter upward gives the signal list more room, matching
    // the native iOS composer behavior; moving it downward gives that room
    // back to the message list.
    const nextHeight = resize.startHeight + resize.startY - event.clientY;
    setSignalPanelHeight(Math.max(56, Math.min(220, nextHeight)));
  };
  const stopSignalResize = (event: PointerEvent<HTMLDivElement>) => {
    if (signalResizeRef.current) event.currentTarget.releasePointerCapture(event.pointerId);
    signalResizeRef.current = null;
  };

  // ONE lookup, not three near-duplicate scans - color/emoji/number all
  // come from whichever TemplateSignal this message actually matches, so
  // they can never disagree with each other (one used to derive its emoji
  // from parsed message text while the others matched by name - see
  // SignalMedallion's own history). Prefers message.signalNumber (the
  // AUTHORITATIVE identity a resolved signalRef/signalNumber send now
  // always carries - see types.ts's Message.signalNumber and
  // backend/workflows/signal/create.wsl's StoreSignalNumber) over matching
  // `type` text: two messages that are genuinely the same Signal always
  // carry the same number regardless of how each one arrived (manual tap,
  // agent, webhook, Cue), where matching by name alone is only a coincidence
  // - it breaks the moment a signal is renamed, or two different templates
  // happen to use the same name for different things. Name matching remains
  // the fallback for a message with no number yet (a plain `to: "name"`
  // mapping output, or one sent before this field existed). Checks this
  // channel's own hydrated templates first, then the global recency list,
  // so the result isn't grey/blank just because the channel's template is
  // outside the capped `templates` window.
  const matchedSignal = (message: Message): TemplateSignal | null => {
    if (!message.type) return null;
    for (const list of [availableTemplates, templates]) {
      for (const t of list) {
        const match = message.signalNumber
          ? t.signals.find((s) => s.number === message.signalNumber)
          : t.signals.find((s) => s.name === message.type);
        if (match) return match;
      }
    }
    return null;
  };
  const colorForSignal = (message: Message): string | null => matchedSignal(message)?.colorHex ?? null;
  const emojiForSignal = (message: Message): string | null => matchedSignal(message)?.emoji ?? null;
  const numberForSignal = (message: Message): string | null => matchedSignal(message)?.number ?? message.signalNumber ?? null;

  // Whether this signal isn't offered by any of the channel's own templates
  // (matchedSignal's first loop) - the owner-only "save as signal" badge
  // (see SaveAsSignalButton below) only makes sense here: a signal that's
  // already in a template has somewhere to be edited from already.
  const isUnknownSignal = (message: Message): boolean =>
    !!message.type &&
    message.type !== ROUND_TYPE &&
    message.type !== REPORT_TYPE &&
    !availableTemplates.some((t) =>
      message.signalNumber ? t.signals.some((s) => s.number === message.signalNumber) : t.signals.some((s) => s.name === message.type),
    );

  // Templates can number 100k+ (see TemplatePickerScreen's comment), so the
  // compose bar only ever hydrates the channel's own selected templates,
  // not `templates` (the capped 20-item global recency list) - a selection
  // made via the search-based picker can easily point outside that window.
  // Shared with ChartScreen via this hook so both panes resolve signal
  // colours against the same fully-hydrated set.
  const availableTemplates = useChannelTemplates(c);

  const selectedTemplate =
    selectedTemplateId === TEXT_OPTION_ID ? null : availableTemplates.find((t) => t.id === selectedTemplateId) ?? null;

  // Keep the composer mode inside what this member is allowed to send
  // (roadmap 4a-f): a signals-only member can't sit on the Text tab, a
  // text-only member can't sit on a template tab. Each branch is guarded
  // by the *other* mode being available - when neither is (postMessages
  // off), the mode is left untouched so the two branches can't ping-pong
  // setState forever.
  useEffect(() => {
    if (!canSendText && canSendSignals && selectedTemplateId === TEXT_OPTION_ID && availableTemplates.length > 0) {
      setSelectedTemplateId(availableTemplates[0].id);
    } else if (!canSendSignals && canSendText && selectedTemplateId !== TEXT_OPTION_ID) {
      setSelectedTemplateId(TEXT_OPTION_ID);
    }
  }, [canSendText, canSendSignals, selectedTemplateId, availableTemplates]);

  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  // Shared by the passive IntersectionObserver (useInfiniteScroll) and the
  // "Load more" button's manual onClick below - loadMoreMessages itself
  // dedupes concurrent calls for the same offset (see StoreContext's
  // queryClient.query usage there), so the two triggers never race.
  const triggerLoadMore = async () => {
    if (!c) return;
    setLoadingMoreMessages(true);
    try {
      await loadMoreMessages(c.id);
    } finally {
      setLoadingMoreMessages(false);
    }
  };
  // Must run before the `if (!c) return null` below (Rules of Hooks) -
  // c?.id/messagesHasMore(c?.id ?? '') degrade harmlessly to hasMore=false
  // for the brief moment c is still undefined.
  const sentinelRef = useInfiniteScroll(triggerLoadMore, c ? messagesHasMore(c.id) : false);

  // Best-effort scroll to a message deep-linked from a push notification
  // (?signal=<id>). Only if it's already in the loaded history - no
  // "load older until found" walk. Fires once per distinct focusSignalId.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const focusedRef = useRef<string | null>(null);

  // Shared by the push-notification deep link below and a tap on a reply's
  // quoted anchor (see ReplyQuote's onClick) - jump to a message already in
  // the loaded history and flash it, no "load older until found" walk.
  const scrollToMessage = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-message-id="${CSS.escape(id)}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setHighlightId(id);
    setTimeout(() => setHighlightId((current) => (current === id ? null : current)), 2500);
  };

  useEffect(() => {
    if (!focusSignalId || !c || focusedRef.current === focusSignalId) return;
    if (!feedMessages.some((m) => m.id === focusSignalId)) return;
    focusedRef.current = focusSignalId;
    scrollToMessage(focusSignalId);
  }, [focusSignalId, c]);

  if (!c) return null;

  // New messages land at the top of the list now (see StoreContext's
  // addMessage), so scroll there on send instead of to the bottom.
  const scrollToNewest = () => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const send = () => {
    // A message may carry text, an attachment, or both - but not neither.
    if (!text.trim() && !pendingAttachment) return;
    sendMessage(
      text.trim(),
      c.id,
      'message',
      undefined,
      undefined,
      pendingAttachment
        ? { key: pendingAttachment.key, type: pendingAttachment.type, name: pendingAttachment.name, size: pendingAttachment.size }
        : undefined,
      replyingToId ?? undefined
    );
    setText('');
    setPendingAttachment(null);
    setReplyingToId(null);
    scrollToNewest();
  };

  const pickAttachment = async (file: File) => {
    setAttachMenuOpen(false);
    setUploadingAttachment(true);
    setComposeError('');
    try {
      setPendingAttachment(await uploadAttachment(file));
    } catch (err) {
      setComposeError(err instanceof Error ? err.message : 'Unable to upload this file.');
    } finally {
      setUploadingAttachment(false);
    }
  };

  // Following a public channel makes you an accepted member server-side
  // (see backend/workflows/stats/channel_follow.wsl), which is what
  // unlocks the compose bar - so "Follow to send signals" is a genuine
  // one-tap join here, not just a feed subscription.
  const joinPublicChannel = async () => {
    setJoining(true);
    setJoinError('');
    try {
      const result = await followChannel(c.nickname, {
        nickname: profile.nickname,
        name: profile.name,
        avatarSymbol: profile.avatarSymbol,
      });
      applyChannelStats(c.id, result);
      // Puts the channel in `channels`, which useChannelMembership reads
      // synchronously to flip to 'accepted' without a member-doc refetch.
      addChannelToPersonalList({ ...c, ...result });
      syncChannels();
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Unable to join this channel.');
    } finally {
      setJoining(false);
    }
  };

  // The actual send, once a value (if any) is settled - `value` is either
  // the signal's static preset (immediate path) or what was typed into the
  // pending-value prompt below, and rides along as Message.value so charts
  // can aggregate by it, not just by count (see chartBuckets.ts's
  // sumByBucket). The message text itself no longer carries the template's
  // title - the signal name/emoji already identify it, and which template
  // it came from is visible in the detail popup (see Message tap handler
  // below) for anyone who needs it.
  const dispatchSignal = (signal: TemplateSignal, value?: string) => {
    const label = value ? `${signal.name}: ${value}` : signal.name;
    sendMessage(`${signal.emoji} ${label}`, c.id, signal.name, value, signal.number, undefined, replyingToId ?? undefined);
    setReplyingToId(null);
    scrollToNewest();
  };

  const sendTemplateSignal = (signalId: string) => {
    const signal = selectedTemplate?.signals.find((s) => s.id === signalId);
    if (!signal || !selectedTemplate) return;
    if (signal.promptForValue) {
      setPendingSignal(signal);
      setPendingValue(signal.value ?? '');
      return;
    }
    dispatchSignal(signal, signal.value);
  };

  const confirmPendingSignal = () => {
    if (!pendingSignal || !selectedTemplate) return;
    dispatchSignal(pendingSignal, pendingValue.trim() || undefined);
    setPendingSignal(null);
    setPendingValue('');
  };

  // Once SaveAsSignalSheet has saved the signal into a template (existing
  // or brand new), make sure that template is actually usable from this
  // channel's composer right away instead of just existing somewhere -
  // otherwise "add to a template" from inside a channel wouldn't visibly
  // fix the "you don't have this yet" gap that opened the sheet.
  const handleSignalSaved = async (template: Template) => {
    if (c && !(c.templateIds ?? []).includes(template.nickname)) {
      await updateChannel({ ...c, templateIds: [...(c.templateIds ?? []), template.nickname] });
    }
  };

  // Seed name for SaveAsSignalSheet: an unknown signal message already has
  // the real name in `type`; a plain text message has no name at all, so
  // its own text stands in as a starting point (fully editable in the
  // sheet either way).
  const signalSourceName = signalSourceMessage
    ? signalSourceMessage.type || displayText(signalSourceMessage).trim().slice(0, 60)
    : '';

  // Renders one message's bubble - either a signal medallion or a plain
  // text card - exactly as a standalone row. Shared by the ordinary feed
  // (group.length === 1) and by the expanded-bunch Sheet below, so a
  // message looks and behaves identically (reply/save-as-signal/detail tap)
  // whether or not it's currently folded into a stack. `onAfterAction`
  // fires alongside any of those taps - the bunch Sheet uses it to close
  // itself once the tap has opened its own overlay (detail/reply/save),
  // rather than leaving the Sheet sitting open behind it.
  const renderMessageBubble = (message: Message, onAfterAction?: () => void) => {
    const isMine = messageBelongsToProfile(message, profile.nickname);
    const renderedMessage = isMine === message.isMine ? message : { ...message, isMine };
    const signalColor = colorForSignal(message);
    const signalEmoji = emojiForSignal(message);
    if (message.type === ROUND_TYPE) {
      return (
        <div className="flex justify-center">
          <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-600/10 px-3 py-1 rounded-full">
            {roundActionLabel(message.value)}
          </span>
        </div>
      );
    }
    if (isMaskedAnswer(message, round)) {
      return (
        <div className="flex justify-start">
          <AppCard compact fitWidth className="max-w-[85%] sm:max-w-[70%] flex items-center gap-2 text-black/50 dark:text-white/50">
            <EyeOff size={14} className="shrink-0" />
            <span className="text-[12px]">
              <span className="font-semibold">{message.sender}</span> answered
            </span>
          </AppCard>
        </div>
      );
    }
    return (
      <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
        {message.type ? (
          // items-end/items-start must follow isMine, same as the row's
          // own justify-end/justify-start above - without it, this column
          // can render wider than SignalMedallion's own content (long
          // wrapped text, e.g. a report) while items-start still pins the
          // medallion to ITS left edge, so a long "mine" message looked
          // like only the avatar made it to the right side while the text
          // stayed flush left.
          <div className={`flex flex-col gap-1.5 ${isMine ? 'items-end' : 'items-start'}`}>
            <SignalMedallion
              message={renderedMessage}
              colorHex={signalColor}
              templateEmoji={signalEmoji}
              onClick={() => { setDetailMessage(renderedMessage); onAfterAction?.(); }}
              onSaveAsSignal={
                owner && isUnknownSignal(message) ? () => { setSignalSourceMessage(renderedMessage); onAfterAction?.(); } : undefined
              }
              onReply={(e) => { e.stopPropagation(); setReplyingToId(message.id); onAfterAction?.(); }}
              replyAnchor={message.replyToId ? messagesById.get(message.replyToId) : undefined}
              onJumpToReply={message.replyToId ? () => scrollToMessage(message.replyToId!) : undefined}
            />
            {message.attachment && <AttachmentView attachment={message.attachment} compact />}
          </div>
        ) : (
          <AppCard
            compact
            fitWidth
            onClick={() => { setDetailMessage(renderedMessage); onAfterAction?.(); }}
            className="max-w-[85%] sm:max-w-[70%] flex flex-col gap-1 cursor-pointer active:scale-[0.98] transition-transform"
          >
            <div className="flex items-start justify-between gap-3">
              <span className="text-[11px] font-semibold text-black/50 dark:text-white/50">{message.sender}</span>
              <span className="text-[10px] text-black/40 dark:text-white/40 shrink-0 whitespace-nowrap flex items-center gap-1">
                <ReplyButton onClick={(e) => { e.stopPropagation(); setReplyingToId(message.id); onAfterAction?.(); }} />
                {owner && displayText(message).trim() && (
                  <SaveAsSignalButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSignalSourceMessage(renderedMessage);
                      onAfterAction?.();
                    }}
                  />
                )}
                {formatBubbleTime(message.date)}
                {isMine && <SyncStatusIcon syncStatus={message.syncStatus} />}
              </span>
            </div>
            {message.replyToId && (
              <ReplyQuote
                anchor={messagesById.get(message.replyToId)}
                onClick={() => scrollToMessage(message.replyToId!)}
              />
            )}
            {displayText(message).trim() && (
              <LinkifiedText text={displayText(message)} className="text-[13px] whitespace-pre-wrap" />
            )}
            {message.attachment && <AttachmentView attachment={message.attachment} />}
          </AppCard>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto flex flex-col gap-3 pb-3"
        // Tapping anywhere that isn't inside the currently-scattered-open
        // group closes it back into a stack - "click somewhere else"
        // collapses it, same as tapping a specific message inside it
        // (reply/save/detail) is still just interacting with that message.
        onClick={(e) => {
          if (!expandedGroupId) return;
          if (!(e.target as HTMLElement).closest(`[data-bunch-group="${expandedGroupId}"]`)) setExpandedGroupId(null);
        }}
      >
        {feedGroups.map((group, groupIndex) => {
          const message = group[0];
          const previous = feedGroups[groupIndex - 1];
          const previousLast = previous?.[previous.length - 1];
          // Messages render newest-first (see StoreContext's addMessage),
          // so a day boundary shows up as this group's date differing from
          // the previous (chronologically later) group already rendered
          // above it - the separator belongs directly above the first
          // group of each day regardless of scan direction.
          const showDaySeparator = !previousLast || !isSameDay(message.date, previousLast.date);
          const expanded = expandedGroupId === message.id;
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              // Off-screen rows skip layout/paint (native virtualization, no
              // scroll-anchoring bugs): with up to ~1000 rows in the DOM this
              // keeps a busy channel cheap. `auto <px>` remembers each row's
              // real height once rendered, so the scrollbar doesn't jump.
              style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 64px' }}
              className={`flex flex-col gap-3 rounded-xl transition-colors ${
                group.some((m) => m.id === highlightId) ? 'bg-blue-500/10 ring-1 ring-blue-500/30' : ''
              }`}
            >
              {showDaySeparator && (
                <div className="flex justify-center">
                  <span className="text-[11px] font-medium text-black/50 dark:text-white/50 bg-black/5 dark:bg-white/10 px-3 py-1 rounded-full">
                    {formatDaySeparator(message.date)}
                  </span>
                </div>
              )}
              {group.length === 1 ? (
                renderMessageBubble(message)
              ) : (
                <BunchStack
                  messages={group}
                  isMine={messageBelongsToProfile(message, profile.nickname)}
                  expanded={expanded}
                  onToggle={() => setExpandedGroupId(expanded ? null : message.id)}
                  renderMessage={renderMessageBubble}
                />
              )}
            </div>
          );
        })}
        {/* Messages render newest-first (index 0 at top) - older pages
            load in at the bottom, so the sentinel belongs at the end of
            this list, not the top. A clickable fallback, not just a passive
            spinner: message bunching (or just a short history) can render
            a page far shorter than the viewport, leaving nothing to
            scroll - the IntersectionObserver in useInfiniteScroll now
            re-triggers on its own in that case, but this button is a
            guaranteed manual way to keep going regardless. */}
        {feedHasMore && (
          <div ref={sentinelRef} className="flex justify-center py-3">
            <button
              onClick={triggerLoadMore}
              disabled={loadingMoreMessages}
              className="flex items-center gap-2 text-[13px] text-black/50 dark:text-white/50 disabled:opacity-60"
            >
              <Loader2 size={16} className={loadingMoreMessages ? 'animate-spin' : ''} />
              {loadingMoreMessages ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      {round.phase === 'collecting' && (
        <div className="mb-2 rounded-xl bg-blue-600/10 text-blue-700 dark:text-blue-300 px-3 py-2 text-[13px] flex items-center gap-2">
          <EyeOff size={14} className="shrink-0" />
          <span>
            {owner
              ? `Round open — ${round.answeredCount} answered so far. Tap ✕ in the header to reveal all.`
              : round.iAnswered
                ? 'Answer submitted — you can change it until the owner reveals.'
                : 'Round open — send your answer. It stays hidden until the owner reveals.'}
          </span>
        </div>
      )}

      {!canPost ? (
        <AppCard className="flex flex-col gap-2 mb-2 items-start">
          {membership === 'loading' ? (
            <span className="text-sm text-black/50 dark:text-white/50">Checking access…</span>
          ) : c.isPublic ? (
            <>
              <span className="text-sm text-black/70 dark:text-white/70">Follow this channel to send signals.</span>
              <Button onClick={joinPublicChannel} disabled={joining || !isLoggedIn}>
                {joining ? 'Following…' : 'Follow to send signals'}
              </Button>
              {!isLoggedIn && <span className="text-xs text-black/40 dark:text-white/40">Log in to follow this channel.</span>}
            </>
          ) : (
            <>
              <span className="text-sm text-black/70 dark:text-white/70">
                You’re not a member of this channel, so you can’t send signals here.
              </span>
              <Button variant="bordered" onClick={() => navigate(`/channels/${c.id}/details`)}>
                Channel details
              </Button>
            </>
          )}
        </AppCard>
      ) : !owner && !perms.postMessages ? (
        // A member the owner has muted: just the notice - no input, send or schema chips.
        <AppCard className="mb-2">
          <span className="text-sm text-black/60 dark:text-white/60">You can't send messages in this channel.</span>
        </AppCard>
      ) : (
      <AppCard className="flex min-w-0 flex-col gap-2 mb-2 overflow-hidden">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-sm font-medium text-black/70 dark:text-white/70">Schema</span>
          <div className="flex min-w-0 gap-2 overflow-x-auto pb-1">
            {canSendText && (
              <button
                onClick={() => setSelectedTemplateId(TEXT_OPTION_ID)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
                  selectedTemplateId === TEXT_OPTION_ID
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-black/15 dark:border-white/20 hover:bg-black/5 dark:hover:bg-white/10'
                }`}
              >
                Text
              </button>
            )}
            {canSendSignals &&
              availableTemplates?.map((t) => (
                <TemplateChip key={t.id} template={t} isSelected={selectedTemplateId === t.id} onSelect={() => setSelectedTemplateId(t.id)} />
              ))}
          </div>
          {/* Restriction note (roadmap 4a-f) - only for a real member who's
              been limited, never the join card's audience. */}
          {canPost && !owner && perms.postMessages && perms.textOnly && (
            <span className="text-xs text-black/50 dark:text-white/50">You can send plain text only in this channel.</span>
          )}
          {canPost && !owner && perms.postMessages && perms.signalsOnly && (
            <span className="text-xs text-black/50 dark:text-white/50">You can send signals only in this channel.</span>
          )}
        </div>

        {/* Not scoped to the text tab (see below) - a reply can be armed
            from a signal/report bubble while the Quick Replies tray is the
            active tab, and it needs to stay visible (and cancelable) no
            matter which tab you answer from, since dispatchSignal threads
            replyingToId exactly like the text form's send() does. */}
        {replyingToId && (
          <div className="flex items-center gap-2">
            <ReplyQuote anchor={messagesById.get(replyingToId)} className="flex-1 min-w-0" />
            <button
              type="button"
              onClick={() => setReplyingToId(null)}
              aria-label="Cancel reply"
              className="shrink-0 text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {selectedTemplateId === TEXT_OPTION_ID && (
          <div className="flex flex-col gap-2">
            {(pendingAttachment || uploadingAttachment) && (
              <div className="flex items-center gap-2 self-start rounded-xl border border-black/10 dark:border-white/15 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1.5 text-[13px]">
                {uploadingAttachment ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span className="text-black/60 dark:text-white/60">Uploading…</span>
                  </>
                ) : (
                  <>
                    <Paperclip size={14} className="text-black/40 dark:text-white/40" />
                    <span className="truncate max-w-[180px]">{pendingAttachment!.name}</span>
                    <button
                      type="button"
                      onClick={() => setPendingAttachment(null)}
                      aria-label="Remove attachment"
                      className="text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex min-w-0 gap-2"
            >
              {/* Reveal-round control (planning poker / hidden voting) -
                  owner-only, and only when enabled on the channel (edit
                  form). Play starts a round (answers hidden); X stops it
                  and reveals every answer at once. See lib/round.ts. */}
              {isChannelOwner(c) && c.revealRounds && (
                <button
                  type="button"
                  onClick={() => doRound(round.phase === 'collecting' ? 'close' : 'open')}
                  disabled={roundBusy}
                  aria-label={
                    round.phase === 'collecting'
                      ? `Reveal answers${round.answeredCount > 0 ? ` (${round.answeredCount} answered)` : ''}`
                      : 'Start round'
                  }
                  className={`shrink-0 rounded-xl border px-3 py-2.5 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50 ${
                    round.phase === 'collecting'
                      ? 'border-red-500/40 text-red-600 dark:text-red-400'
                      : 'border-blue-500/40 text-blue-600 dark:text-blue-400'
                  }`}
                >
                  {roundBusy ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : round.phase === 'collecting' ? (
                    <X size={18} />
                  ) : (
                    <Play size={18} />
                  )}
                </button>
              )}
              {(canAttach || canImportCsv) && (
                <button
                  type="button"
                  onClick={() => setAttachMenuOpen(true)}
                  aria-label="Attach a file"
                  disabled={uploadingAttachment}
                  className="shrink-0 rounded-xl border border-black/15 dark:border-white/20 px-3 py-2.5 text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                >
                  <Plus size={18} />
                </button>
              )}
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message"
                disabled={!canSendText}
                className="min-w-0 flex-1 rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!canSendText || (!text.trim() && !pendingAttachment)}
                className="shrink-0 rounded-xl px-4 py-2.5 bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Send
              </button>
            </form>
          </div>
        )}

        {pendingSignal && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              confirmPendingSignal();
            }}
            className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex sm:items-center"
          >
            <span className="col-span-2 min-w-0 truncate text-sm sm:shrink-0">
              {pendingSignal.emoji} {pendingSignal.name}
            </span>
            <input
              type="text"
              autoFocus
              value={pendingValue}
              onChange={(e) => setPendingValue(e.target.value)}
              placeholder="Value"
              className="min-w-0 flex-1 rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
            />
            <Button type="submit" className="shrink-0">Send</Button>
            <Button
              type="button"
              variant="bordered"
              className="col-span-2 justify-self-end sm:col-auto sm:justify-self-auto sm:shrink-0"
              onClick={() => {
                setPendingSignal(null);
                setPendingValue('');
              }}
            >
              Cancel
            </Button>
          </form>
        )}

        {selectedTemplate && !pendingSignal && (
          <>
            <div
              role="separator"
              aria-label="Resize signal list"
              aria-orientation="horizontal"
              onPointerDown={startSignalResize}
              onPointerMove={moveSignalResize}
              onPointerUp={stopSignalResize}
              onPointerCancel={stopSignalResize}
              className="h-3 shrink-0 cursor-row-resize touch-none rounded-full bg-black/[0.04] dark:bg-white/[0.06] after:mx-auto after:block after:h-1 after:w-12 after:translate-y-1 after:rounded-full after:bg-black/15 dark:after:bg-white/20"
            />
            <div className="flex min-w-0 content-start flex-wrap gap-2 overflow-y-auto pb-1" style={{ height: signalPanelHeight }}>
              {selectedTemplate.signals?.map((signal) => (
                <button
                  key={signal.id}
                  onClick={() => sendTemplateSignal(signal.id)}
                  disabled={!canSendSignals}
                  className="shrink-0 self-start rounded-full border border-black/15 dark:border-white/20 pl-1 pr-3 py-1 text-sm flex items-center gap-2 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                >
                {/* Same medallion look as a sent signal message (see
                    SignalMedallion below) - shown here before the tap
                    that produces one. A signal that asks for a value on
                    send (promptForValue) carries a small pencil badge so
                    it's clear a tap opens a value prompt, not an instant
                    send. */}
                <span className="relative shrink-0">
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${isMultiCharSignalIcon(signal.emoji) ? 'text-xs font-semibold' : 'text-base'}`}
                    style={{ background: `${signal.colorHex}1f`, borderColor: signal.colorHex }}
                  >
                    {signal.emoji}
                  </span>
                  {signal.promptForValue && (
                    <span
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white dark:bg-[#1c1c1e] border border-black/15 dark:border-white/25 flex items-center justify-center"
                      aria-label="asks for a value"
                    >
                      <Pencil size={9} className="text-black/60 dark:text-white/60" />
                    </span>
                  )}
                </span>
                <span className="font-medium">{signal.name}</span>
                {signal.value && <span className="text-black/50 dark:text-white/50">{signal.value}</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </AppCard>
      )}

      {joinError && <Toast message={joinError} onDismiss={() => setJoinError('')} />}
      {composeError && <Toast message={composeError} onDismiss={() => setComposeError('')} />}
      <CsvImportErrorBanner rows={csvSkippedRows} onDismiss={() => setCsvSkippedRows([])} />

      <input
        ref={attachInputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void pickAttachment(file);
          if (attachInputRef.current) attachInputRef.current.value = '';
        }}
      />

      <Sheet open={attachMenuOpen} onClose={() => setAttachMenuOpen(false)} title="Add to message">
        <div className="flex flex-col gap-2 py-1">
          {canAttach && (
            <button
              type="button"
              onClick={() => attachInputRef.current?.click()}
              className="flex items-center gap-3 rounded-xl border border-black/10 dark:border-white/15 px-4 py-3 text-left hover:bg-black/5 dark:hover:bg-white/10"
            >
              <Paperclip size={18} className="text-black/50 dark:text-white/50" />
              <span className="flex flex-col">
                <span className="text-sm font-medium">Photo or file</span>
                <span className="text-xs text-black/50 dark:text-white/50">Image or PDF, up to 10 MB</span>
              </span>
            </button>
          )}
          {canImportCsv && (
            <button
              type="button"
              onClick={() => {
                setAttachMenuOpen(false);
                setCsvImportOpen(true);
              }}
              className="flex items-center gap-3 rounded-xl border border-black/10 dark:border-white/15 px-4 py-3 text-left hover:bg-black/5 dark:hover:bg-white/10"
            >
              <Upload size={18} className="text-black/50 dark:text-white/50" />
              <span className="flex flex-col">
                <span className="text-sm font-medium">Import from CSV</span>
                <span className="text-xs text-black/50 dark:text-white/50">Bulk-add rows as messages</span>
              </span>
            </button>
          )}
          {owner && mappingsOn && (
            <button
              type="button"
              onClick={() => {
                setAttachMenuOpen(false);
                setAttachMappingOpen(true);
              }}
              className="flex items-center gap-3 rounded-xl border border-black/10 dark:border-white/15 px-4 py-3 text-left hover:bg-black/5 dark:hover:bg-white/10"
            >
              <Workflow size={18} className="text-black/50 dark:text-white/50" />
              <span className="flex flex-col">
                <span className="text-sm font-medium">Point a mapping here</span>
                <span className="text-xs text-black/50 dark:text-white/50">Redirect an existing integration's data to this channel</span>
              </span>
            </button>
          )}
        </div>
      </Sheet>

      <AttachMappingSheet
        open={attachMappingOpen}
        onClose={() => setAttachMappingOpen(false)}
        channelNickname={c.nickname}
        channelTitle={c.title || c.nickname}
        onAttached={(mapping) => setComposeError(`Pointed "${mapping.title || mapping.nickname}" at this channel.`)}
      />

      <CsvMessageImportSheet
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        channelNickname={c.nickname}
        availableSignals={availableTemplates.flatMap((t) => t.signals)}
        onImported={(result) => {
          setComposeError(
            `Imported ${result.importedCount} message${result.importedCount === 1 ? '' : 's'}${
              result.skippedCount > 0 ? ` (${result.skippedCount} skipped)` : ''
            }.`
          );
          setCsvSkippedRows(result.skippedRows);
          void loadMessages(c.id);
        }}
      />

      <SaveAsSignalSheet
        open={signalSourceMessage !== null}
        onClose={() => setSignalSourceMessage(null)}
        initialName={signalSourceName}
        initialValue={signalSourceMessage?.value}
        onSaved={handleSignalSaved}
      />

      <Sheet open={detailMessage !== null} onClose={() => setDetailMessage(null)} title="Message details">
        {detailMessage && (
          <div className="flex flex-col gap-4">
            {/* The calculated Output Value leads when the signal has one: the business
                value and its period first, technical detail collapsed beneath it. */}
            {detailMessage.calc && <SignalValueCard calc={detailMessage.calc} name={detailMessage.type} />}
            <DetailRow label="From" value={detailMessage.sender} />
            <DetailRow label="Sent" value={formatDate(detailMessage.date)} />
            {/* Event time (backend `at`) - only worth showing when it's
                meaningfully different from `date`, i.e. the signal came
                from an integration that supplied its own observation time. */}
            {detailMessage.at && detailMessage.at.slice(0, 16) !== detailMessage.date.slice(0, 16) && (
              <DetailRow label="Event time" value={formatDate(detailMessage.at)} />
            )}
            <DetailRow
              label={detailMessage.type === REPORT_TYPE ? 'Report' : 'Message'}
              value={
                detailMessage.type === REPORT_TYPE ? (
                  <MarkdownReportText text={displayText(detailMessage)} className="text-[14px] flex flex-col gap-1 break-words" />
                ) : (
                  <LinkifiedText text={displayText(detailMessage)} />
                )
              }
            />
            {detailMessage.labels && detailMessage.labels.length > 0 && (
              <DetailRow
                label="Labels"
                value={
                  <span className="flex flex-wrap gap-1.5">
                    {detailMessage.labels?.map((l) => (
                      <span
                        key={l}
                        className="rounded-md bg-black/[0.06] dark:bg-white/10 text-black/60 dark:text-white/60 text-xs font-mono px-1.5 py-0.5"
                      >
                        {l}
                      </span>
                    ))}
                  </span>
                }
              />
            )}
            {detailMessage.attachment && (
              <DetailRow label="Attachment" value={<AttachmentView attachment={detailMessage.attachment} />} />
            )}
            {detailMessage.isMine && (
              <DetailRow
                label="Server"
                value={
                  detailMessage.syncStatus === 'pending'
                    ? 'Sending…'
                    : detailMessage.syncStatus === 'failed'
                      ? 'Not saved - couldn’t reach the server'
                      : 'Saved'
                }
              />
            )}
            {detailMessage.type && (
              <DetailRow
                label="Signal"
                value={
                  <span className="flex items-center gap-1.5">
                    {detailMessage.type}
                    {numberForSignal(detailMessage) ? (
                      <span className="rounded-md bg-black/[0.06] dark:bg-white/10 text-black/60 dark:text-white/60 text-xs font-mono px-1.5 py-0.5">
                        #{numberForSignal(detailMessage)}
                      </span>
                    ) : (
                      detailMessage.type !== ROUND_TYPE &&
                      detailMessage.type !== REPORT_TYPE && (
                        <span className="text-xs text-black/40 dark:text-white/40">no number yet</span>
                      )
                    )}
                  </span>
                }
              />
            )}
            {detailMessage.value && !detailMessage.calc?.value.display && <DetailRow label="Value" value={detailMessage.value} />}
          </div>
        )}
      </Sheet>
    </div>
  );
}

// The owner-only "add this as a signal" affordance on a message that isn't
// in any of this channel's templates yet - a plain typed message, or a
// signal whose name (message.type) doesn't match any of availableTemplates'
// signals (see isUnknownSignal above). A small solid Plus badge (not a
// leading glyph lost in the row) with a soft "shine" - a duplicate circle
// behind it running Tailwind's ping animation - so it reads as a live
// suggestion worth tapping, not just another static icon. Opens
// SaveAsSignalSheet seeded from that message. `centered` dead-centers it
// over a parent's own circle (see SignalMedallion below) instead of the
// plain-text message's inline placement (the timestamp row).
// A clearly-clickable reply affordance - a bare small glyph read poorly
// as "a button" sitting among the sender/timestamp meta row's plain text,
// so this gets its own rounded hover background (same treatment
// SaveAsSignalButton's badge gets, scaled down) rather than just a color
// change on the icon itself.
function ReplyButton({ onClick }: { onClick: (e: MouseEvent) => void }) {
  return (
    <button
      type="button"
      title="Reply"
      aria-label="Reply"
      onClick={onClick}
      className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-black/40 dark:text-white/40 hover:bg-blue-600 hover:text-white active:scale-90 transition-colors"
    >
      <Reply size={12} />
    </button>
  );
}

function SaveAsSignalButton({ onClick, centered }: { onClick: (e: MouseEvent) => void; centered?: boolean }) {
  const size = centered ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <button
      type="button"
      onClick={onClick}
      title="Not in any of your schemas - save as a signal?"
      aria-label="Save as a signal"
      className={`shrink-0 inline-flex items-center justify-center rounded-full bg-amber-500 text-white shadow-sm ring-2 ring-white dark:ring-[#1c1c1e] hover:bg-amber-600 active:scale-95 transition-transform ${size} ${
        // top-1/2 + -translate-1/2 centers on the parent box's own
        // dimensions, unlike inset-0 + m-auto (which drifted off-center
        // whenever the parent's rendered box didn't exactly match the
        // circle it visually wraps).
        centered ? 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : 'relative'
      }`}
    >
      <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-75" />
      <Plus size={centered ? 13 : 10} strokeWidth={3.5} className="relative" />
    </button>
  );
}

// Experimental: signal-triggered messages as a round "medallion" badge
// instead of the normal rectangular bubble - the emoji is the whole point
// of a signal tap, so it gets to be the focal shape instead of just a
// small leading glyph inside a text line.
// Message bunching's collapsed row (see MessagesScreen's feedGroups) - a
// stack of overlapping card edges standing in for `messages.length` bubbles
// from the same sender, close together in time. Tapping it opens the full
// list (MessagesScreen's Sheet); the front card previews the most recent
// message's text so the group isn't a total black box while collapsed.
function previewTextFor(message: Message): string {
  return message.type ? splitSignalEmoji(displayText(message)).label || displayText(message) : displayText(message);
}

// Message bunching's collapsed row (see MessagesScreen's feedGroups) - real
// (if clipped) previews of every bunched message, overlapping like a small
// deck of cards, standing in for `messages.length` bubbles from the same
// sender close together in time. Tapping the deck scatters it open in
// place (`expanded`); each message lands in its own normal, fully
// interactive row instead of a single collapsed sheet/popover.
function BunchStack({
  messages,
  isMine,
  expanded,
  onToggle,
  renderMessage,
}: {
  messages: Message[];
  isMine: boolean;
  expanded: boolean;
  onToggle: () => void;
  renderMessage: (message: Message) => ReactNode;
}) {
  const groupId = messages[0].id;
  if (expanded) {
    return (
      <div data-bunch-group={groupId} className="flex flex-col gap-2">
        {messages.map((m, i) => (
          <BunchScatterItem key={m.id} index={i}>
            {renderMessage(m)}
          </BunchScatterItem>
        ))}
      </div>
    );
  }
  // Collapsed: the front (newest) message renders normally and defines the
  // stack's size; up to 2 older messages behind it peek out as same-sized
  // card backs, nudged down and sideways (never up - that would bleed into
  // the row above) and faded/scaled down further back. Plain divs rather
  // than AppCard/fitWidth: `inset-0` needs an unconstrained width to
  // stretch to the front card's exact size, and fitWidth's `w-fit` would
  // fight that, shrinking each peek to its own tiny content width instead
  // (the bug this replaced - mismatched, misaligned peek sizes).
  const peeks = messages.slice(1, 3);
  const front = messages[0];
  return (
    <div data-bunch-group={groupId} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
      <button onClick={onToggle} className="relative max-w-[85%] sm:max-w-[70%] text-left active:scale-[0.98] transition-transform">
        {/* Hidden anchors so scrollToMessage/highlightId can still target a
            message folded behind the front card. */}
        {peeks.map((m) => (
          <span key={m.id} data-message-id={m.id} className="sr-only" aria-hidden />
        ))}
        {[...peeks].reverse().map((m, revIndex) => {
          const depth = peeks.length - revIndex; // furthest-back peek has the highest depth
          return (
            <div
              key={m.id}
              aria-hidden
              // Purely horizontal, toward the side the bubble is already
              // anchored to (left for a left-aligned/their bubble, right
              // for a right-aligned/mine one) - "peeking out to the side",
              // not a diagonal smear. A solid-ish fill (not AppCard's
              // usual near-transparent glass look) so it actually reads as
              // a card edge against the dark background instead of
              // vanishing into it.
              className="absolute inset-0 rounded-2xl border border-black/15 dark:border-white/15 bg-black/[0.06] dark:bg-white/[0.14]"
              style={{
                transform: `translateX(${(isMine ? -1 : 1) * depth * 10}px)`,
                opacity: 1 - depth * 0.28,
                zIndex: -depth,
              }}
            />
          );
        })}
        <AppCard compact fitWidth className="relative flex flex-col gap-1">
          <div className="flex items-start justify-between gap-3">
            <span className="text-[11px] font-semibold text-black/50 dark:text-white/50">{front.sender}</span>
            <span className="text-[10px] text-black/40 dark:text-white/40 shrink-0 whitespace-nowrap">
              {messages.length} messages
            </span>
          </div>
          {previewTextFor(front).trim() && (
            <span className="text-[13px] text-black/70 dark:text-white/70 truncate block">{previewTextFor(front)}</span>
          )}
        </AppCard>
      </button>
    </div>
  );
}

// A bunched message's entrance once its group scatters open - starts offset
// (alternating left/right per index, so the group visibly fans apart
// rather than all sliding the same direction) and transitions to its
// normal resting position, staggered slightly by index.
function BunchScatterItem({ index, children }: { index: number; children: ReactNode }) {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const direction = index % 2 === 0 ? 1 : -1;
  return (
    <div
      className="transition-all duration-300 ease-out"
      style={{
        transform: settled ? 'translate(0, 0) scale(1)' : `translate(${direction * 22}px, 0px) scale(0.92)`,
        opacity: settled ? 1 : 0,
        transitionDelay: `${Math.min(index, 6) * 40}ms`,
      }}
    >
      {children}
    </div>
  );
}

function SignalMedallion({
  message,
  colorHex,
  templateEmoji,
  onClick,
  onSaveAsSignal,
  onReply,
  replyAnchor,
  onJumpToReply,
}: {
  message: Message;
  colorHex: string | null;
  // The matched template's own configured emoji for this signal, when one
  // exists - takes priority over whatever's embedded in the message TEXT
  // (splitSignalEmoji below), since only a manually-tapped signal ever
  // embeds one there at all (sendMessage's "${signal.emoji} ${label}").
  // An integration-ingested Signal with the exact same name/number would
  // otherwise always render as a blank circle, even once it's resolving to
  // the identical signal a manual tap would.
  templateEmoji: string | null;
  onClick: () => void;
  // Set only for the owner, on a signal not offered by any of this
  // channel's templates yet - renders as a Plus badge dead-centered over
  // the medallion's own circle (see SaveAsSignalButton's `centered` mode)
  // rather than a second element competing with it for room in the row.
  onSaveAsSignal?: () => void;
  onReply: (e: MouseEvent) => void;
  // A signal can itself be a reply now (dispatchSignal threads replyingToId
  // same as a typed message) - these mirror the plain-text bubble's
  // ReplyQuote so tapping a signal's own reply quote jumps/highlights the
  // same way. Both undefined when message.replyToId isn't set.
  replyAnchor?: Message;
  onJumpToReply?: () => void;
}) {
  const { emoji: textEmoji, label } =
    message.type === REPORT_TYPE ? { emoji: REPORT_EMOJI, label: displayText(message) } : splitSignalEmoji(displayText(message));
  const emoji = message.type === REPORT_TYPE ? textEmoji : templateEmoji || textEmoji || DEFAULT_SIGNAL_EMOJI;
  const accent = colorHex ?? '#6B7280';
  return (
    <div
      className={`flex items-end gap-2.5 max-w-[85%] sm:max-w-[70%] ${message.isMine ? 'flex-row-reverse' : ''}`}
    >
      {/* Fixed at exactly the circle's own size (not sized from the button's
          content box, which carries a browser-default border/padding that
          previously threw the centered badge off to one side) - a relative
          wrapper, not a button itself, since SaveAsSignalButton below is a
          sibling `<button>` overlapping this box, and a <button> can't
          contain another <button> (invalid HTML, and the outer one would
          swallow clicks meant for the badge). */}
      <div className="relative w-12 h-12 shrink-0">
        <button onClick={onClick} className="absolute inset-0 rounded-full p-0 border-0 active:scale-[0.98] transition-transform">
          <div
            className={`w-full h-full rounded-full flex items-center justify-center border-2 shadow-sm ${isMultiCharSignalIcon(emoji) ? 'text-base font-semibold' : 'text-2xl'} ${onSaveAsSignal ? 'opacity-30' : ''}`}
            style={{ background: `${accent}1f`, borderColor: accent }}
          >
            {emoji}
          </div>
        </button>
        {onSaveAsSignal && (
          <SaveAsSignalButton
            centered
            onClick={(e) => {
              e.stopPropagation();
              onSaveAsSignal();
            }}
          />
        )}
      </div>
      {/* flex-1 (flex-basis 0, not auto/content-based) rather than relying on
          shrink-from-content: a <button>'s intrinsic sizing in flex layouts
          doesn't reliably honor min-w-0 for wrapping text across browsers,
          which is what let long signal names paint out over the circle
          instead of wrapping inside their own column. */}
      {/* A div, not a button: a report is Markdown with links in it, and a link
          (or table, list, code block) can't validly sit inside a <button>. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        className="flex flex-1 min-w-0 cursor-pointer flex-col gap-0.5 text-left active:scale-[0.98] transition-transform"
      >
        <div className={`flex items-center gap-2 ${message.isMine ? 'flex-row-reverse' : ''}`}>
          <span className="text-[11px] font-semibold text-black/50 dark:text-white/50">{message.sender}</span>
          <span className="text-[10px] text-black/40 dark:text-white/40 whitespace-nowrap flex items-center gap-1">
            {formatBubbleTime(message.date)}
            {message.isMine && <SyncStatusIcon syncStatus={message.syncStatus} />}
          </span>
        </div>
        {message.replyToId && (
          <ReplyQuote anchor={replyAnchor} onClick={onJumpToReply} />
        )}
        {/* Full name, wrapped rather than ellipsized - a signal name like
            "disk_used_percent" has no spaces for the browser's default
            wrapping to break on, so break-words (overflow-wrap) is what
            actually lets it fold onto a second line instead of just
            overflowing the row. */}
        {message.type === REPORT_TYPE ? (
          <ClampedMarkdown text={label} lines={10} className="text-[13px] flex flex-col gap-1 break-words [&_p]:font-normal" />
        ) : (
          <span className="text-[13px] font-medium">{label}</span>
        )}
      </div>
      {/* A sibling, not nested in the label button above - a <button>
          can't validly contain another <button> (same reasoning as
          SaveAsSignalButton's own comment on the medallion circle). */}
      <ReplyButton onClick={onReply} />
    </div>
  );
}

// Only shown on messages this account sent (message.isMine) - a message
// someone else sent is, by definition, already on the server, since that's
// the only place this client could have learned about it. undefined
// syncStatus means "saved" (the common/default case, including every
// message loaded from history via GET /signals, which never carries a
// syncStatus at all).
function SyncStatusIcon({ syncStatus }: { syncStatus: Message['syncStatus'] }) {
  if (syncStatus === 'pending') {
    return <Clock size={11} className="text-black/30 dark:text-white/30 shrink-0" aria-label="Sending…" />;
  }
  if (syncStatus === 'failed') {
    return <AlertCircle size={11} className="text-red-500 shrink-0" aria-label="Not saved - tap for details" />;
  }
  return <Check size={11} className="text-black/30 dark:text-white/30 shrink-0" aria-label="Saved" />;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-black/50 dark:text-white/50">{label}</span>
      <div className="text-[15px]">{value}</div>
    </div>
  );
}
